// THE CHANNELS OF EVERY MODEL, READ THROUGH THEIR TAXONOMY AND THEIR MODEL'S METADATA.
//
// A model document carries `channel_taxonomy`, keyed by the same channel keys its
// reference_model.config.media uses (and its report and its pretty names), each entry naming a value
// per level: { L0: { name, abbreviation }, …, L5, L6: 'free text' }. Nothing here reads the level
// registry — the values a browser can filter on are the values models actually carry, and the
// registry is not readable by every role that can compose a model.
//
// Each row also carries its model's metadata (see modelFacets.js), so one list can be narrowed by
// what a channel is AND by which business it was measured in. The faceting itself is modelFacets'.
//
// Pure functions only, so the faceting can be reasoned about apart from the dialog drawing it.
import { displayName } from '@/utils/ekrmFromModel.js'
import { MODEL_FACET_KEYS, modelMetadata, searchText } from '@/utils/modelFacets.js'

export const TAXONOMY_FACET_LEVELS = ['L0', 'L1', 'L2', 'L3', 'L4', 'L5']
export const TAXONOMY_CUSTOM_LEVEL = 'L6'
export const CHANNEL_FACET_KEYS = MODEL_FACET_KEYS.concat(TAXONOMY_FACET_LEVELS)

export function taxonomyRowId (modelId, raw) {
  return modelId + '::' + raw
}

// The channels a model offers. Its media config is the list the taxonomy editor itself walks; a
// document without one still has whatever channels were classified on it.
function channelKeys (model) {
  const config = model.reference_model && model.reference_model.config
  const media = config && config.media
  if (media && typeof media === 'object' && Object.keys(media).length > 0) return Object.keys(media)
  const taxonomy = model.channel_taxonomy
  return taxonomy && typeof taxonomy === 'object' ? Object.keys(taxonomy) : []
}

function levelName (entry, level) {
  const value = entry && entry[level]
  if (!value || typeof value !== 'object') return null
  const name = String(value.name || '').trim()
  return name.length > 0 ? name : null
}

// One row per model × channel, with everything the browser filters, searches and draws.
// `labelFor(key, value)` is how a facet value is shown, so it can be searched the way it reads.
export function taxonomyRows (models, labelFor, countryKey) {
  const rows = []
  ;(models || []).forEach(model => {
    if (!model || !model.id) return
    const taxonomy = model.channel_taxonomy && typeof model.channel_taxonomy === 'object'
      ? model.channel_taxonomy
      : {}
    const modelName = String(model.name || model.id)
    const metadata = modelMetadata(model, countryKey)
    channelKeys(model).forEach(raw => {
      const entry = taxonomy[raw] || null
      const facets = Object.assign({}, metadata)
      const trail = []
      TAXONOMY_FACET_LEVELS.forEach(level => {
        const name = levelName(entry, level)
        facets[level] = name === null ? [] : [name]
        if (name !== null) trail.push(name)
      })
      const custom = entry && typeof entry[TAXONOMY_CUSTOM_LEVEL] === 'string'
        ? entry[TAXONOMY_CUSTOM_LEVEL].trim()
        : ''
      const label = displayName(model, raw) || String(raw)
      rows.push({
        id: taxonomyRowId(model.id, raw),
        modelId: model.id,
        modelName: modelName,
        raw: raw,
        label: label,
        facets: facets,
        trail: trail,
        custom: custom,
        classified: trail.length > 0 || custom.length > 0,
        search: searchText([label, String(raw), modelName, custom], facets, labelFor)
      })
    })
  })
  return rows
}

export function taxonomyBreadcrumb (row) {
  return row.trail.join(' › ')
}

// Grouped by model, so a model's channels stand together under its header; inside a model the
// classified channels come first, in the order of their taxonomy, then by name.
export function compareTaxonomyRows (a, b) {
  const byModel = a.modelName.localeCompare(b.modelName) || String(a.modelId).localeCompare(String(b.modelId))
  if (byModel !== 0) return byModel
  if (a.trail.length === 0 && b.trail.length > 0) return 1
  if (b.trail.length === 0 && a.trail.length > 0) return -1
  return taxonomyBreadcrumb(a).localeCompare(taxonomyBreadcrumb(b)) ||
    a.label.localeCompare(b.label)
}
